import { useTranslation } from '@/lib/i18n';
import { Button, Input, RequiredMark } from '@/components/ui';
import { Plus, Trash2 } from 'lucide-react';
import type { OptionGroupInput } from '../schemas/productSchema';

export interface OptionGroupsEditorProps {
  value: ReadonlyArray<OptionGroupInput>;
  onChange: (next: OptionGroupInput[]) => void;
  disabled?: boolean;
}

const emptyChoice = () => ({ label: '', additionalAmount: null });
const emptyGroup = (): OptionGroupInput => ({ name: '', required: true, choices: [emptyChoice()] });

/** 그룹별 선택지 수의 곱(데카르트 조합 수). 상한 100(BE/스키마 정합) 안내용. */
const comboCount = (groups: ReadonlyArray<OptionGroupInput>): number =>
  groups.reduce((acc, g) => acc * Math.max(1, g.choices.length), groups.length > 0 ? 1 : 0);

/**
 * #10c 분리형 조합 옵션 편집기(등록 전용) — 옵션명+필수여부+선택지(라벨/추가금) 행 편집.
 * 1개 이상 입력 시 평면 옵션 대신 그룹 데카르트 곱으로 조합이 생성된다(BE 계약). 조합 상한 100.
 * 불변 갱신(매 변경마다 새 배열) — RHF Controller 가 보유한 값과 동기화.
 */
export const OptionGroupsEditor = ({ value, onChange, disabled = false }: OptionGroupsEditorProps) => {
  const { t } = useTranslation('products');
  const groups = value;

  const updateGroup = (gi: number, patch: Partial<OptionGroupInput>) =>
    onChange(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));

  const addGroup = () => onChange([...groups, emptyGroup()]);
  const removeGroup = (gi: number) => onChange(groups.filter((_, i) => i !== gi));

  const updateChoice = (gi: number, ci: number, patch: Partial<OptionGroupInput['choices'][number]>) => {
    const group = groups[gi];
    if (!group) return;
    updateGroup(gi, { choices: group.choices.map((c, i) => (i === ci ? { ...c, ...patch } : c)) });
  };
  const addChoice = (gi: number) => {
    const group = groups[gi];
    if (!group) return;
    updateGroup(gi, { choices: [...group.choices, emptyChoice()] });
  };
  const removeChoice = (gi: number, ci: number) => {
    const group = groups[gi];
    if (!group) return;
    updateGroup(gi, { choices: group.choices.filter((_, i) => i !== ci) });
  };

  const combos = comboCount(groups);

  return (
    <div className="flex flex-col gap-3" data-testid="option-groups-editor">
      <p className="text-[12px] leading-relaxed" style={{ color: 'var(--content-muted)' }}>
        {t('products.optionGroups.hint', {
          defaultValue:
            '옵션 그룹(예: 색상, 사이즈)과 선택지를 입력하면 그룹 조합으로 옵션이 자동 생성됩니다. 그룹을 1개 이상 추가하면 위 평면 옵션 대신 조합 옵션이 적용됩니다.',
        })}
      </p>

      {groups.length === 0 ? (
        <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {t('products.optionGroups.empty', { defaultValue: '조합 옵션 미사용 — 그룹을 추가하면 사용합니다.' })}
        </p>
      ) : null}

      {groups.map((group, gi) => (
        <div
          key={gi}
          className="flex flex-col gap-3 rounded-md p-3"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
          data-testid="option-group-row"
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-[200px] flex-1 flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-strong)' }}>
                {t('products.optionGroups.groupName', { defaultValue: '옵션명' })}
                <RequiredMark />
              </span>
              <Input
                value={group.name}
                onChange={(e) => updateGroup(gi, { name: e.target.value })}
                placeholder={t('products.optionGroups.groupNamePlaceholder', { defaultValue: '예: 색상' })}
                maxLength={100}
                disabled={disabled}
              />
            </div>
            <label
              className="inline-flex items-center gap-2 pb-2 text-[13px]"
              style={{ color: 'var(--content-default)' }}
            >
              <input
                type="checkbox"
                checked={group.required ?? true}
                onChange={(e) => updateGroup(gi, { required: e.target.checked })}
                disabled={disabled}
                aria-label={t('products.optionGroups.required', { defaultValue: '필수 선택' })}
              />
              {t('products.optionGroups.required', { defaultValue: '필수 선택' })}
            </label>
            {!disabled ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => removeGroup(gi)}
                aria-label={t('products.optionGroups.removeGroup', { defaultValue: '그룹 삭제' })}
              >
                <Trash2 size={14} aria-hidden="true" />
                {t('products.optionGroups.removeGroup', { defaultValue: '그룹 삭제' })}
              </Button>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('products.optionGroups.choices', { defaultValue: '선택지' })}
            </span>
            {group.choices.map((choice, ci) => (
              <div key={ci} className="flex flex-wrap items-center gap-2" data-testid="option-choice-row">
                <Input
                  value={choice.label}
                  onChange={(e) => updateChoice(gi, ci, { label: e.target.value })}
                  placeholder={t('products.optionGroups.choiceLabelPlaceholder', { defaultValue: '예: 블랙' })}
                  maxLength={100}
                  disabled={disabled}
                  className="min-w-[160px] flex-1"
                  aria-label={t('products.optionGroups.choiceLabel', { defaultValue: '선택지 라벨' })}
                />
                <Input
                  type="number"
                  min={0}
                  step="1"
                  inputMode="numeric"
                  value={choice.additionalAmount ?? ''}
                  onChange={(e) => {
                    const next = e.target.valueAsNumber;
                    updateChoice(gi, ci, { additionalAmount: Number.isFinite(next) ? next : null });
                  }}
                  placeholder={t('products.optionGroups.additionalAmount', { defaultValue: '추가금(원)' })}
                  disabled={disabled}
                  className="max-w-[140px] tabular-nums"
                  aria-label={t('products.optionGroups.additionalAmount', { defaultValue: '추가금(원)' })}
                />
                {!disabled && group.choices.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeChoice(gi, ci)}
                    aria-label={t('products.optionGroups.removeChoice', { defaultValue: '선택지 삭제' })}
                    style={{ color: 'var(--signal-danger)', cursor: 'pointer' }}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            ))}
            {!disabled ? (
              <div>
                <Button type="button" variant="outline" size="sm" onClick={() => addChoice(gi)}>
                  <Plus size={14} aria-hidden="true" />
                  {t('products.optionGroups.addChoice', { defaultValue: '선택지 추가' })}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      ))}

      {!disabled ? (
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="sm" onClick={addGroup} data-testid="option-group-add">
            <Plus size={15} aria-hidden="true" />
            {t('products.optionGroups.addGroup', { defaultValue: '옵션 그룹 추가' })}
          </Button>
          {groups.length > 0 ? (
            <span
              className="text-[12px] tabular-nums"
              style={{ color: combos > 100 ? 'var(--status-danger-fg)' : 'var(--content-muted)' }}
              data-testid="option-combos-count"
            >
              {t('products.optionGroups.combos', {
                count: combos,
                defaultValue: `생성될 조합 ${combos}개 (최대 100)`,
              })}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
